/**
 * CMS section schema — the contract between the live website and the
 * Admin → CMS Pages editor.
 *
 * Every section listed here maps 1:1 to a section that is actually rendered
 * on the storefront, in the same order. Defaults hold the professional demo
 * copy that ships with the site, so the editor is always fully populated
 * even before anything has been saved, and the rendered page is identical
 * whether or not the operator has edited it.
 */

export type CmsFieldType =
  | "text"
  | "textarea"
  | "image"
  | "video"
  | "product"
  | "toggle"
  | "url"
  | "number"
  | "list"
  | "info";

export type CmsFieldDef = {
  key: string;
  label: string;
  type: CmsFieldType;
  help?: string;
  /** For `list` fields — the shape of one repeatable item. */
  itemFields?: CmsFieldDef[];
  /** For `list` fields — a new blank item. */
  itemDefaults?: Record<string, string>;
  /** For `list` fields — label template key used in the item header. */
  itemLabelKey?: string;
};

export type CmsSectionValue = Record<string, string | Array<Record<string, string>>>;

export type CmsSectionDef = {
  key: string;
  icon: string;
  label: string;
  description: string;
  fields: CmsFieldDef[];
  defaults: CmsSectionValue;
};

const cta = (labelKey: string, hrefKey: string, label: string, href: string) => ({
  fields: [
    { key: labelKey, label: `${label} label`, type: "text" as const },
    { key: hrefKey, label: `${label} link`, type: "url" as const },
  ],
  defaults: { [labelKey]: label, [hrefKey]: href },
});

/* ------------------------------------------------------------------ home */

const HOME: CmsSectionDef[] = [
  {
    key: "hero",
    icon: "🏠",
    label: "Hero Banner",
    description: "Full-width hero slider — 4 independently editable slides (image, text, CTA).",
    fields: [
      { key: "eyebrow", label: "Slide 1 — Eyebrow", type: "text" },
      { key: "title", label: "Slide 1 — Main heading", type: "text" },
      { key: "highlight", label: "Slide 1 — Highlight text (gold, italic)", type: "text" },
      { key: "titleAfter", label: "Slide 1 — Heading after highlight", type: "text" },
      { key: "description", label: "Slide 1 — Description", type: "textarea" },
      { key: "cta1Label", label: "Slide 1 — CTA label", type: "text" },
      { key: "cta1Href", label: "Slide 1 — CTA link", type: "url" },
      { key: "image", label: "Hero Image 1", type: "image", help: "Leave empty to use the first catalogue image." },
      { key: "mobileImage", label: "Slide 1 — Mobile hero image", type: "image", help: "Optional. Used below 640px." },

      { key: "eyebrow2", label: "Slide 2 — Eyebrow", type: "text" },
      { key: "title2", label: "Slide 2 — Main heading", type: "text" },
      { key: "highlight2", label: "Slide 2 — Highlight text", type: "text" },
      { key: "titleAfter2", label: "Slide 2 — Heading after highlight", type: "text" },
      { key: "description2", label: "Slide 2 — Description", type: "textarea" },
      { key: "cta2Label", label: "Slide 2 — CTA label", type: "text" },
      { key: "cta2Href", label: "Slide 2 — CTA link", type: "url" },
      { key: "image2", label: "Hero Image 2", type: "image", help: "Optional — slide 2 is hidden when empty." },

      { key: "eyebrow3", label: "Slide 3 — Eyebrow", type: "text" },
      { key: "title3", label: "Slide 3 — Main heading", type: "text" },
      { key: "highlight3", label: "Slide 3 — Highlight text", type: "text" },
      { key: "titleAfter3", label: "Slide 3 — Heading after highlight", type: "text" },
      { key: "description3", label: "Slide 3 — Description", type: "textarea" },
      { key: "cta3Label", label: "Slide 3 — CTA label", type: "text" },
      { key: "cta3Href", label: "Slide 3 — CTA link", type: "url" },
      { key: "image3", label: "Hero Image 3", type: "image", help: "Optional — slide 3 is hidden when empty." },

      { key: "eyebrow4", label: "Slide 4 — Eyebrow", type: "text" },
      { key: "title4", label: "Slide 4 — Main heading", type: "text" },
      { key: "highlight4", label: "Slide 4 — Highlight text", type: "text" },
      { key: "titleAfter4", label: "Slide 4 — Heading after highlight", type: "text" },
      { key: "description4", label: "Slide 4 — Description", type: "textarea" },
      { key: "cta4Label", label: "Slide 4 — CTA label", type: "text" },
      { key: "cta4Href", label: "Slide 4 — CTA link", type: "url" },
      { key: "image4", label: "Hero Image 4", type: "image", help: "Optional — slide 4 is hidden when empty." },

      { key: "primaryLabel", label: "Secondary CTA label (slide 1)", type: "text" },
      { key: "primaryHref", label: "Secondary CTA link (slide 1)", type: "url" },
      { key: "imageAlt", label: "Image alt text", type: "text" },
      { key: "background", label: "Background colour", type: "text", help: "Hex value, e.g. #faf7f2" },
      { key: "noteLeft", label: "Footnote (left)", type: "text" },
      { key: "noteRight", label: "Footnote (right)", type: "text" },
    ],
    defaults: {
      eyebrow: "Maison CB16 — Est. 2016",
      title: "The Art of",
      highlight: "Invisible",
      titleAfter: "Luxury",
      description:
        "Rare essences, hand-blended in small ateliers. A signature scent is the most intimate accessory a person can wear — we craft yours like couture.",
      cta1Label: "Shop All",
      cta1Href: "/shop",
      image: "",

      eyebrow2: "New Arrivals",
      title2: "Discover the",
      highlight2: "Newest",
      titleAfter2: "Compositions",
      description2:
        "Fresh from the atelier — limited flacons and seasonal accords, released in small batches.",
      cta2Label: "Fragrances",
      cta2Href: "/fragrances",
      image2: "",

      eyebrow3: "Curated Collections",
      title3: "Scents for",
      highlight3: "Every",
      titleAfter3: "Occasion",
      description3:
        "Explore our curated houses — woody, floral, oriental and fresh — composed for day and night.",
      cta3Label: "Collections",
      cta3Href: "/collections",
      image3: "",

      eyebrow4: "Discovery Packs",
      title4: "Find Your",
      highlight4: "Signature",
      titleAfter4: "Scent",
      description4:
        "Hand-assembled 10 ML discovery sets — the finest way to meet the CB16 wardrobe.",
      cta4Label: "Discover Packs",
      cta4Href: "/packs",
      image4: "",

      primaryLabel: "Our Story",
      primaryHref: "/about",
      mobileImage: "",
      imageAlt: "CB16 signature perfume",
      background: "#faf7f2",
      noteLeft: "Free Global Shipping",
      noteRight: "Complimentary Samples",
    },
  },

  {
    key: "films",
    icon: "🎬",
    label: "Fragrance Films",
    description: "The shoppable video rail. Videos themselves are managed in Content → Video Gallery.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Section title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
      { key: "limit", label: "Number of videos", type: "number" },
      {
        key: "cards",
        label: "Featured video cards",
        type: "list",
        help: "Leave empty to keep showing the videos from Content → Video Gallery.",
        itemLabelKey: "cardTitle",
        itemDefaults: { hidden: "" },
        itemFields: [
          { key: "video", label: "Video source", type: "video", help: "Pick a Video Gallery item or upload through the media pipeline." },
          { key: "thumbnail", label: "Thumbnail", type: "image", help: "Optional. Falls back to the gallery or product image." },
          { key: "cardTitle", label: "Video title (optional)", type: "text" },
          { key: "productId", label: "Featured product", type: "product" },
          { key: "overrideImage", label: "Override product image", type: "image" },
          { key: "overrideTitle", label: "Override display title", type: "text" },
          { key: "overrideDescription", label: "Override description", type: "textarea" },
          { key: "hidden", label: "Visibility", type: "toggle" },
        ],
      },
    ],
    defaults: {
      eyebrow: "Fragrance Films",
      title: "Experience the Fragrance",
      subtitle: "Watch each fragrance come alive before adding it to your collection.",
      limit: "5",
    },
  },
  {
    key: "maison",
    icon: "🌿",
    label: "Our Maison",
    description: "Brand story block with image, two paragraphs and a link to the About page.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "highlight", label: "Highlight text", type: "text" },
      { key: "titleAfter", label: "Heading after highlight", type: "text" },
      { key: "body1", label: "Paragraph 1", type: "textarea" },
      { key: "body2", label: "Paragraph 2", type: "textarea" },
      { key: "image", label: "Image", type: "image" },
      { key: "imageAlt", label: "Image alt text", type: "text" },
      ...cta("ctaLabel", "ctaHref", "Read Our Story", "/about").fields,
    ],
    defaults: {
      eyebrow: "Our Maison",
      title: "A decade of",
      highlight: "olfactive",
      titleAfter: "obsession.",
      body1:
        "Founded in Mumbai, CB16 is a house of independent perfumers. We source rare naturals — Turkish rose, Laotian oud, Calabrian bergamot — and compose in small batches, never rushed, never reformulated.",
      body2:
        "Every flacon is numbered by hand. Every scent is meant to become, in time, indistinguishable from the person who wears it.",
      image: "",
      imageAlt: "A CB16 perfumer blending rare essences by hand",
      ctaLabel: "Read Our Story",
      ctaHref: "/about",
    },
  },
  {
    key: "collections",
    icon: "🛍",
    label: "Featured Collections",
    description: "Three collection cards. The cards themselves come from Admin → Collections.",
    fields: [
      { key: "eyebrow", label: "Section eyebrow", type: "text" },
      { key: "title", label: "Section heading", type: "text" },
      { key: "description", label: "Subtitle", type: "textarea" },
      { key: "cards", label: "Collection cards", type: "info", help: "Published collections, managed in Admin → Collections." },
    ],
    defaults: {
      eyebrow: "Featured Collections",
      title: "Compositions for every hour",
      description: "Three olfactive worlds, each hand-composed by our master perfumers.",
    },
  },
  {
    key: "bestsellers",
    icon: "🔥",
    label: "Best Sellers",
    description: "Product grid of products flagged as Bestseller in Admin → Products.",
    fields: [
      { key: "eyebrow", label: "Section eyebrow", type: "text" },
      { key: "title", label: "Section heading", type: "text" },
      { key: "description", label: "Subtitle", type: "textarea" },
      { key: "limit", label: "Display limit", type: "number" },
      ...cta("ctaLabel", "ctaHref", "Shop All", "/fragrances").fields,
      { key: "products", label: "Product selection", type: "info", help: "Products with the Bestseller badge (Admin → Products)." },
    ],
    defaults: {
      eyebrow: "Best Sellers",
      title: "The house favorites",
      description: "The scents our wearers return to, season after season.",
      limit: "4",
      ctaLabel: "Shop All",
      ctaHref: "/fragrances",
    },
  },
  {
    key: "newarrivals",
    icon: "✨",
    label: "New Arrivals",
    description: "Product grid of the most recently created products.",
    fields: [
      { key: "eyebrow", label: "Section eyebrow", type: "text" },
      { key: "title", label: "Section heading", type: "text" },
      { key: "description", label: "Subtitle", type: "textarea" },
      { key: "limit", label: "Display limit", type: "number" },
      ...cta("ctaLabel", "ctaHref", "Shop All", "/fragrances").fields,
      { key: "products", label: "Product selection", type: "info", help: "Newest products by creation date (Admin → Products)." },
    ],
    defaults: {
      eyebrow: "New Arrivals",
      title: "Just poured, just numbered",
      description: "Freshly batched from the atelier — available while the run lasts.",
      limit: "4",
      ctaLabel: "Shop All",
      ctaHref: "/fragrances",
    },
  },
  {
    key: "why",
    icon: "💎",
    label: "Why CB16",
    description: "Dark band with four feature cards and icons.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      {
        key: "cards",
        label: "Feature cards",
        type: "list",
        itemLabelKey: "title",
        itemFields: [
          { key: "icon", label: "Icon", type: "text", help: "feather · leaf · sparkles · package · gem · flask" },
          { key: "title", label: "Title", type: "text" },
          { key: "body", label: "Description", type: "textarea" },
        ],
        itemDefaults: { icon: "sparkles", title: "New pillar", body: "Describe this pillar." },
      },
    ],
    defaults: {
      eyebrow: "Why CB16",
      title: "The maison difference.",
      cards: [
        { icon: "feather", title: "Hand-Composed", body: "Each formula is blended in-atelier, never outsourced." },
        { icon: "leaf", title: "Rare Naturals", body: "Sourced from single-estate growers around the world." },
        { icon: "sparkles", title: "Numbered Batches", body: "Small runs of no more than 300 flacons per batch." },
        { icon: "package", title: "Signature Ritual", body: "Complimentary samples with every order, always." },
      ],
    },
  },
  {
    key: "wearers",
    icon: "❤️",
    label: "Wearers · Testimonials",
    description: "Customer quotes shown between Why CB16 and the Journal strip.",
    fields: [
      { key: "eyebrow", label: "Section eyebrow", type: "text" },
      { key: "title", label: "Section heading", type: "text" },
      {
        key: "items",
        label: "Testimonials",
        type: "list",
        itemLabelKey: "author",
        itemFields: [
          { key: "quote", label: "Quote", type: "textarea" },
          { key: "author", label: "Customer name", type: "text" },
          { key: "context", label: "City / context", type: "text" },
        ],
        itemDefaults: { quote: "A few words from a happy wearer.", author: "Name", context: "City" },
      },
    ],
    defaults: {
      eyebrow: "Wearers",
      title: "Words from the maison",
      items: [
        {
          quote: "Ambre Noir has become my invisible signature. Strangers stop me to ask what it is — I never tell.",
          author: "Isabelle M.",
          context: "Paris",
        },
        {
          quote: "The craftsmanship is on another level. It smells like nothing else on my shelf, and that's saying something.",
          author: "Julian R.",
          context: "New York",
        },
        {
          quote: "Elegant packaging, thoughtful samples, and a scent that lasts from morning meetings until midnight.",
          author: "Amara D.",
          context: "Milan",
        },
      ],
    },
  },
  {
    key: "packs",
    icon: "📦",
    label: "Discovery Packs",
    description: "Discovery pack rail shown between Wearers and the Journal strip.",
    fields: [
      { key: "eyebrow", label: "Section eyebrow", type: "text" },
      { key: "title", label: "Section heading", type: "text" },
      { key: "description", label: "Section description", type: "textarea" },
      { key: "limit", label: "Display count", type: "number" },
      { key: "background", label: "Background colour", type: "text", help: "Hex value, e.g. #faf7f2. Leave empty for white." },
      { key: "image", label: "Section image", type: "image", help: "Optional. Used as a fallback pack image." },
      ...cta("ctaLabel", "ctaHref", "Explore all packs", "/packs").fields,
      {
        key: "featured",
        label: "Featured Discovery Packs",
        type: "list",
        itemLabelKey: "slug",
        itemFields: [
          { key: "slug", label: "Pack slug", type: "text", help: "As shown in Admin \u2192 Packs. Leave the list empty to show the newest published packs." },
          { key: "title", label: "Title override", type: "text" },
          { key: "description", label: "Description override", type: "textarea" },
          { key: "image", label: "Image override", type: "image" },
        ],
        itemDefaults: { slug: "", title: "", description: "", image: "" },
      },
    ],
    defaults: {
      eyebrow: "Discovery Packs",
      title: "Compose your own discovery set",
      description:
        "Choose your bottles one by one from the CB16 atelier. Live pricing, live progress, and a pack that is entirely yours.",
      limit: "3",
      background: "#faf7f2",
      image: "",
      ctaLabel: "Explore all packs",
      ctaHref: "/packs",
      featured: [],
    },
  },
  {
    key: "journal",
    icon: "📰",
    label: "Journal Strip",
    description: "Latest journal entries flagged “Show on homepage” in Admin → Blog.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      ...cta("ctaLabel", "ctaHref", "Read the journal", "/blog").fields,
      { key: "posts", label: "Articles", type: "info", help: "Published posts marked “Show on homepage” (Admin → Blog)." },
    ],
    defaults: {
      eyebrow: "CB16 · Journal",
      title: "Notes from the Atelier",
      ctaLabel: "Read the journal",
      ctaHref: "/blog",
    },
  },
  {
    key: "consultation",
    icon: "📞",
    label: "Private Appointment",
    description: "Full-width image banner inviting visitors to book a consultation.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image", label: "Background image", type: "image" },
      ...cta("ctaLabel", "ctaHref", "Reserve a Consultation", "/contact").fields,
    ],
    defaults: {
      eyebrow: "Private Appointment",
      title: "Find your signature, guided by a perfumer.",
      description:
        "Book a one-hour consultation, in-boutique or virtual, and leave with three curated flacons chosen for you alone.",
      image: "",
      ctaLabel: "Reserve a Consultation",
      ctaHref: "/contact",
    },
  },
  {
    key: "newsletter",
    icon: "📧",
    label: "Newsletter",
    description: "Dark newsletter band with the subscription form.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "highlight", label: "Highlight text", type: "text" },
      { key: "titleAfter", label: "Heading after highlight", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "placeholder", label: "Email placeholder", type: "text" },
      { key: "buttonLabel", label: "Button text", type: "text" },
      { key: "successMessage", label: "Success message", type: "textarea" },
    ],
    defaults: {
      eyebrow: "Le Journal CB16",
      title: "Invitations, editions,",
      highlight: "rituals",
      titleAfter: ".",
      description:
        "Receive first access to limited flacons, private appointments, and essays from our perfumers. Never more than twice a month.",
      placeholder: "Your email address",
      buttonLabel: "Subscribe",
      successMessage: "Thank you. You'll receive our latest perfume journal updates.",
    },
  },
  {
    key: "instagram",
    icon: "📷",
    label: "@maisoncb16 · Instagram Gallery",
    description: "Six-tile social gallery at the foot of the homepage.",
    fields: [
      { key: "eyebrow", label: "Section eyebrow (handle)", type: "text" },
      { key: "title", label: "Section heading", type: "text" },
      { key: "profileUrl", label: "Instagram link", type: "url" },
      {
        key: "tiles",
        label: "Gallery images",
        type: "list",
        itemLabelKey: "alt",
        itemFields: [
          { key: "image", label: "Image", type: "image" },
          { key: "alt", label: "Alt text", type: "text" },
          { key: "href", label: "Post link", type: "url" },
        ],
        itemDefaults: { image: "", alt: "", href: "" },
      },
    ],
    defaults: {
      eyebrow: "@maisoncb16",
      title: "Follow the maison",
      profileUrl: "https://instagram.com",
      tiles: [],
    },
  },
  {
    key: "footer",
    icon: "🦶",
    label: "Footer",
    description: "Site-wide footer — brand blurb, link columns, legal links and copyright.",
    fields: [
      { key: "logo", label: "Footer logo", type: "image", help: "Leave empty to use the store name wordmark." },
      { key: "brandName", label: "Brand wordmark", type: "text", help: "Leave empty to use the name from Admin → Settings." },
      { key: "tagline", label: "Brand description", type: "textarea", help: "Leave empty to use Admin → Settings → Footer tagline." },
      {
        key: "columns",
        label: "Link columns",
        type: "list",
        itemLabelKey: "heading",
        itemFields: [
          { key: "heading", label: "Column heading", type: "text" },
          { key: "links", label: "Links", type: "textarea", help: "One per line as: Label | /path" },
        ],
        itemDefaults: { heading: "New column", links: "" },
      },
      { key: "contactHeading", label: "Contact heading", type: "text" },
      { key: "address", label: "Address", type: "textarea" },
      { key: "email", label: "Email", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "copyright", label: "Copyright text", type: "text", help: "Leave empty to use Admin → Settings → Footer copyright." },
      { key: "bottomText", label: "Footer bottom text", type: "text" },
      { key: "legalLinks", label: "Legal links", type: "textarea", help: "One per line as: Label | /path" },
      { key: "social", label: "Social media links", type: "info", help: "Managed in Admin → Settings → Social profiles." },
      { key: "payments", label: "Payment icons", type: "info", help: "Not shown in the current footer design." },
    ],
    defaults: {
      logo: "",
      brandName: "",
      tagline: "",
      columns: [
        {
          heading: "Maison",
          links: "Our Story | /about\nCollections | /collections\nFAQ | /faq",
        },
        {
          heading: "Client Care",
          links:
            "Contact | /contact\nShipping Policy | /shipping\nRefund & Return Policy | /returns\nPrivacy Policy | /privacy\nTerms & Conditions | /terms",
        },
        {
          heading: "Boutique",
          links: "Fragrances | /fragrances\nShop All | /shop\nDiscovery Packs | /packs\nCorporate Gifting | /corporate\nSearch | /search",
        },
      ],
      contactHeading: "",
      address: "",
      email: "",
      phone: "",
      copyright: "",
      bottomText: "",
      legalLinks: "Privacy | /privacy\nTerms | /terms",
    },
  },
];

/* ----------------------------------------------------------------- about */

const ABOUT: CmsSectionDef[] = [
  {
    key: "hero",
    icon: "🏠",
    label: "Hero",
    description: "Top banner of the About page.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "highlight", label: "Highlight text", type: "text" },
      { key: "titleAfter", label: "Heading after highlight", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image", label: "Hero image", type: "image" },
      { key: "imageAlt", label: "Image alt text", type: "text" },
    ],
    defaults: {
      eyebrow: "The House",
      title: "A modern maison,",
      highlight: "rooted",
      titleAfter: "in the East",
      description:
        "CB16 Atelier composes fragrance the slow way — rare naturals, long macerations, and small batches signed by the hand that blended them.",
      image: "",
      imageAlt: "CB16 flacon resting on stone in warm atelier light",
    },
  },
  {
    key: "story",
    icon: "🌿",
    label: "Our Story · Craftsmanship",
    description: "Image and two paragraphs about how the fragrances are composed.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "body1", label: "Paragraph 1", type: "textarea" },
      { key: "body2", label: "Paragraph 2 (ingredients & sourcing)", type: "textarea" },
      { key: "image", label: "Image", type: "image" },
      { key: "imageAlt", label: "Image alt text", type: "text" },
    ],
    defaults: {
      eyebrow: "Our Story",
      title: "Composed by hand, never by formula alone",
      body1:
        "Every CB16 composition begins on paper strips, not in software. Our perfumers work through dozens of trials before a scent earns a number, and each accepted formula rests for a minimum of six weeks before bottling.",
      body2:
        "We source oud from Assam, rose from Kannauj, jasmine from Grasse, and vetiver from Tamil Nadu — traceable to the distiller, harvest by harvest.",
      image: "",
      imageAlt: "Perfumer weighing raw essences at the CB16 atelier bench",
    },
  },
  {
    key: "founder",
    icon: "👤",
    label: "Founder · Philosophy",
    description: "Founder quote, philosophy paragraph and portrait image.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Quote / heading", type: "textarea" },
      { key: "body", label: "Paragraph", type: "textarea" },
      { key: "attribution", label: "Attribution", type: "text" },
      { key: "image", label: "Image", type: "image" },
      { key: "imageAlt", label: "Image alt text", type: "text" },
    ],
    defaults: {
      eyebrow: "The Founder",
      title: "“A scent should feel like a memory you haven’t had yet.”",
      body:
        "Founded in 2016, CB16 grew out of a private collection of attars kept for three generations. The house still follows the same rule it started with: if a composition cannot be made honestly at small scale, it is not made at all.",
      attribution: "CB16 Atelier — Founder & Head Perfumer",
      image: "",
      imageAlt: "A row of numbered CB16 flacons on a linen surface",
    },
  },
  {
    key: "timeline",
    icon: "📜",
    label: "Timeline",
    description: "Milestones of the house, in order.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      {
        key: "items",
        label: "Milestones",
        type: "list",
        itemLabelKey: "year",
        itemFields: [
          { key: "year", label: "Year", type: "text" },
          { key: "title", label: "Title", type: "text" },
          { key: "body", label: "Description", type: "textarea" },
        ],
        itemDefaults: { year: "2026", title: "New milestone", body: "Describe this milestone." },
      },
    ],
    defaults: {
      eyebrow: "Timeline",
      title: "A decade in small batches",
      items: [
        { year: "2016", title: "The first flacon", body: "CB16 begins as a two-person atelier in Mumbai, blending oud and jasmine in small batches." },
        { year: "2019", title: "The Grasse partnership", body: "A collaboration with a family distillery in Grasse secures our rare naturals supply." },
        { year: "2022", title: "The private collection", body: "Numbered, limited-run flacons introduced for collectors and private clients." },
        { year: "2026", title: "The house today", body: "Nine olfactive categories, still hand-composed, still bottled in batches under 500." },
      ],
    },
  },
  {
    key: "cta",
    icon: "📞",
    label: "Final CTA",
    description: "Closing call to action with two buttons.",
    fields: [
      { key: "title", label: "Heading", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "primaryLabel", label: "Primary CTA label", type: "text" },
      { key: "primaryHref", label: "Primary CTA link", type: "url" },
      { key: "secondaryLabel", label: "Secondary CTA label", type: "text" },
      { key: "secondaryHref", label: "Secondary CTA link", type: "url" },
    ],
    defaults: {
      title: "Begin your olfactive wardrobe",
      description: "Explore the collection, or write to our concierge for a guided recommendation.",
      primaryLabel: "Shop Fragrances",
      primaryHref: "/fragrances",
      secondaryLabel: "Contact the Maison",
      secondaryHref: "/contact",
    },
  },
];

/* --------------------------------------------------------------- contact */

const CONTACT: CmsSectionDef[] = [
  {
    key: "hero",
    icon: "🏠",
    label: "Hero",
    description: "Top banner of the Contact page.",
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
    ],
    defaults: {
      eyebrow: "Concierge",
      title: "Contact the Maison",
      description:
        "Our concierge answers within one business day — for orders, fragrance advice, press, or private consultations.",
    },
  },
  {
    key: "form",
    icon: "✉",
    label: "Contact Form",
    description: "Heading, button and confirmation message of the enquiry form.",
    fields: [
      { key: "title", label: "Heading", type: "text" },
      { key: "buttonLabel", label: "Button text", type: "text" },
      { key: "successMessage", label: "Success message", type: "textarea" },
      { key: "namePlaceholder", label: "Name placeholder", type: "text" },
      { key: "emailPlaceholder", label: "Email placeholder", type: "text" },
      { key: "subjectPlaceholder", label: "Subject placeholder", type: "text" },
      { key: "messagePlaceholder", label: "Message placeholder", type: "text" },
    ],
    defaults: {
      title: "Send a message",
      buttonLabel: "Send Message",
      successMessage: "Thank you — your message has been received. Our concierge will reply shortly.",
      namePlaceholder: "Your name",
      emailPlaceholder: "you@example.com",
      subjectPlaceholder: "Order enquiry, press, consultation…",
      messagePlaceholder: "How can we help?",
    },
  },
  {
    key: "map",
    icon: "🗺",
    label: "Google Map",
    description: "Embedded map under the contact form.",
    fields: [
      { key: "query", label: "Map search query", type: "textarea", help: "Full address used for the embed and directions link." },
      { key: "title", label: "Map title (accessibility)", type: "text" },
    ],
    defaults: {
      query:
        "CB16 Atelier Shop No. 6A/B Krishna Shopping Center Ambedkar Chowk Shivaji Path Thane Maharashtra 400602 India",
      title: "CB16 Atelier showroom location",
    },
  },
  {
    key: "office",
    icon: "🏢",
    label: "Office / Showroom",
    description: "Printed address card with the “Get Directions” button.",
    fields: [
      { key: "title", label: "Heading", type: "text" },
      { key: "address", label: "Address", type: "textarea", help: "One line per row." },
      { key: "directionsLabel", label: "Directions button label", type: "text" },
    ],
    defaults: {
      title: "CB16 Atelier",
      address:
        "Shop No. 6A/B\nKrishna Shopping Center\nAmbedkar Chowk\nShivaji Path\nThane, Maharashtra 400602\nIndia",
      directionsLabel: "Get Directions",
    },
  },
  {
    key: "info",
    icon: "📍",
    label: "Contact Information",
    description: "Sidebar card with address, email, phone and WhatsApp.",
    fields: [
      { key: "title", label: "Heading", type: "text" },
      { key: "details", label: "Address, email, phone", type: "info", help: "Managed in Admin → Settings → Store details." },
    ],
    defaults: { title: "Showroom" },
  },
  {
    key: "hours",
    icon: "🕒",
    label: "Business Hours",
    description: "Sidebar card listing opening hours.",
    fields: [
      { key: "title", label: "Heading", type: "text" },
      { key: "weekdaysLabel", label: "Weekdays label", type: "text" },
      { key: "saturdayLabel", label: "Saturday label", type: "text" },
      { key: "sundayLabel", label: "Sunday label", type: "text" },
      { key: "times", label: "Opening times", type: "info", help: "Managed in Admin → Settings → Business hours." },
    ],
    defaults: {
      title: "Business hours",
      weekdaysLabel: "Monday – Friday",
      saturdayLabel: "Saturday",
      sundayLabel: "Sunday",
    },
  },
  {
    key: "social",
    icon: "❤️",
    label: "Follow the House",
    description: "Sidebar card with social links.",
    fields: [
      { key: "title", label: "Heading", type: "text" },
      { key: "links", label: "Social links", type: "info", help: "Managed in Admin → Settings → Social profiles." },
    ],
    defaults: { title: "Follow the house" },
  },
];

export const PAGE_SECTIONS: Record<string, CmsSectionDef[]> = {
  home: HOME,
  about: ABOUT,
  contact: CONTACT,
};

export function sectionsFor(slug: string): CmsSectionDef[] | null {
  return PAGE_SECTIONS[slug] ?? null;
}

/** Defaults for one section, used by the storefront and the editor alike. */
export function sectionDefaults(slug: string, key: string): CmsSectionValue {
  return sectionsFor(slug)?.find((s) => s.key === key)?.defaults ?? {};
}
